import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { Field, TextArea, TextInput } from '@l2u/ui/components/Field.tsx';
import { StringListEditor } from '../components/StringListEditor.tsx';
import { OptionListEditor } from '../components/OptionListEditor.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { errorText } from '@l2u/ui/lib/errorText.ts';
import { EmptyState } from '@l2u/ui/components/EmptyState.tsx';
import { VerticalTabs } from '../components/VerticalTabs.tsx';
import { AdminUsers } from './Users.tsx';
import { usePortalSettings } from '@l2u/ui/lib/portalSettings.tsx';
import { useConfirm } from '../lib/confirm.tsx';
import { useToast } from '../lib/toast.tsx';
import { COMPANY_INFO_LABEL_DEFAULTS, type PortalSettings } from '@l2u/shared/schemas';
import { normalizeReminderDays } from '@l2u/shared/reminders';
import type { QuestionCategory } from '@l2u/ui/lib/types.ts';
import { SETTINGS_TABS, resolveSettingsTab } from '../lib/settingsTabs.ts';
import { sortOptionList } from '../lib/optionListView.ts';

/** The settings keys backed by an OptionListPanel. */
type OptionListKey = 'contractorTypeOptions' | 'clusterRegionOptions' | 'stationOptions';

export function AdminSettings() {
  const qc = useQueryClient();
  const { reload } = usePortalSettings();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => adminApi.get<{ settings: Partial<PortalSettings> }>('/api/settings'),
  });

  // Unsaved edits layered over the fetched settings, rather than a copy of them.
  // This is deliberately NOT `useState({})` + `useEffect(() => setForm(settings))`:
  // an effect commits one render LATE, so the first render after `isLoading`
  // flips to false would hand the panels an empty form. StringListEditor seeds
  // its rows once and ignores later `value` changes, so a panel mounted on that
  // render — which is what a refresh or bookmark on `?tab=document-analysis`
  // does, a supported entry point (see settingsTabs.ts and App.tsx's /users and
  // /activity redirects) — would render an empty list over stored values, and
  // the next Save would persist that emptiness.
  const [edits, setEdits] = useState<Partial<PortalSettings> | null>(null);
  const [error, setError] = useState<unknown>(null);
  const form = edits ?? data?.settings ?? {};

  const showToast = useToast();

  const saveMutation = useMutation({
    mutationFn: (input: Partial<PortalSettings>) => adminApi.put('/api/settings', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void reload();
      showToast('Settings saved');
    },
    // Toast as well as setError: the Contractor Types, Cluster Regions, Stations
    // and Vendor Information panels render no <ErrorMessage>, so without this a
    // failed save on those tabs reports nothing at all.
    onError: (err) => {
      setError(err);
      showToast(`Couldn't save settings — ${errorText(err, 'please try again')}`, 'error');
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveSettingsTab(searchParams.get('tab'));
  const setActiveTab = (key: string) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('tab', key);
        return p;
      },
      { replace: true },
    );

  if (isLoading) return <CenteredSpinner />;

  function set<K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) {
    setEdits((prev) => ({ ...(prev ?? data?.settings ?? {}), [key]: value }));
  }

  function save() {
    setError(null);
    saveMutation.mutate(form);
  }

  /**
   * Save for the option-list tabs: alphabetises the list as part of the save,
   * rather than while the admin types. Entries stay where they were added until
   * Save, so a newly added row does not jump away mid-edit.
   *
   * The sorted list is written to `edits` as well as sent, so the panel can
   * re-seed its editor from it (see OptionListPanel). It is deliberately NOT
   * `set(key, …)` followed by `save()`: `set` is an async state update, so
   * `save` would still read the pre-sort `form`.
   */
  function saveOptionList(key: OptionListKey, list: string[]) {
    setError(null);
    const next = { ...form, [key]: sortOptionList(list) };
    setEdits(next);
    saveMutation.mutate(next);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Manage portal configuration</p>
      </div>
      <div className="flex gap-6">
        <VerticalTabs
          tabs={SETTINGS_TABS}
          active={activeTab}
          onSelect={setActiveTab}
          ariaLabel="Settings sections"
        />
        <div
          className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white p-6"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === 'users' && <AdminUsers />}
          {activeTab === 'content' && (
            <PortalContentTab
              form={form}
              set={set}
              save={save}
              saving={saveMutation.isPending}
              error={error}
            />
          )}
          {activeTab === 'company-info' && (
            <VendorInfoLabelsPanel
              form={form}
              set={set}
              save={save}
              saving={saveMutation.isPending}
            />
          )}
          {activeTab === 'contractor-types' && (
            <OptionListPanel
              title="Contractor Types"
              hint="These become the audience options on questionnaires and the choices on the new-vendor form."
              value={form.contractorTypeOptions ?? []}
              // An emptied list is sent as [], never as undefined: PUT /api/settings
              // MERGES into the stored branding blob, and JSON.stringify drops
              // undefined keys — so an omitted key means "leave as-is", which made
              // removing every entry a silent no-op. Every reader does `?? []`, so a
              // stored [] and an absent key mean the same thing downstream.
              onChange={(list) => set('contractorTypeOptions', list)}
              savedValue={data?.settings?.contractorTypeOptions ?? []}
              itemLabel="contractor type"
              itemLabelPlural="contractor types"
              addLabel="Add contractor type"
              placeholder="e.g. Owner Operator"
              settingsKey="contractorTypeOptions"
              saveList={saveOptionList}
              saving={saveMutation.isPending}
            />
          )}
          {activeTab === 'cluster-regions' && (
            <OptionListPanel
              title="Cluster Regions"
              hint="These become the choices for the Cluster Region dropdown on the new-vendor form and the vendor detail page."
              value={form.clusterRegionOptions ?? []}
              onChange={(list) => set('clusterRegionOptions', list)}
              savedValue={data?.settings?.clusterRegionOptions ?? []}
              itemLabel="cluster region"
              itemLabelPlural="cluster regions"
              addLabel="Add cluster region"
              placeholder="e.g. Metro North"
              settingsKey="clusterRegionOptions"
              saveList={saveOptionList}
              saving={saveMutation.isPending}
            />
          )}
          {activeTab === 'stations' && (
            <OptionListPanel
              title="Stations"
              hint="These become the choices for the Station dropdown on the new-vendor form and the vendor detail page."
              value={form.stationOptions ?? []}
              onChange={(list) => set('stationOptions', list)}
              savedValue={data?.settings?.stationOptions ?? []}
              itemLabel="station"
              itemLabelPlural="stations"
              addLabel="Add station"
              placeholder="e.g. Sydney Depot"
              settingsKey="stationOptions"
              saveList={saveOptionList}
              saving={saveMutation.isPending}
            />
          )}
          {activeTab === 'notifications' && (
            <NotificationsPanel
              form={form}
              set={set}
              save={save}
              saving={saveMutation.isPending}
              error={error}
            />
          )}
          {activeTab === 'document-analysis' && (
            <DocumentAnalysisPanel
              form={form}
              set={set}
              save={save}
              saving={saveMutation.isPending}
              error={error}
            />
          )}
          {activeTab === 'question-categories' && <QuestionCategoriesPanel />}
        </div>
      </div>
    </div>
  );
}

function PortalContentTab({
  form,
  set,
  save,
  saving,
  error,
}: {
  form: Partial<PortalSettings>;
  set: <K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) => void;
  save: () => void;
  saving: boolean;
  error: unknown;
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Portal Content</h2>
        <Btn onClick={save} loading={saving}>
          Save
        </Btn>
      </div>
      <div className="max-w-2xl space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Legal</h2>
        {/* Terms & Conditions moved to the published external document
            (TERMS_URL in @l2u/shared/legal) — Privacy and the Shared
            Commitment stay in-portal. */}
        <Field label="Privacy Policy (Markdown)" hint="Markdown supported">
          <TextArea
            value={form.privacyContent ?? ''}
            onChange={(e) => set('privacyContent', e.target.value)}
          />
        </Field>
        <Field label="Shared Commitment (Markdown)" hint="Markdown supported">
          <TextArea
            value={form.sharedCommitmentContent ?? ''}
            onChange={(e) => set('sharedCommitmentContent', e.target.value)}
          />
        </Field>

        <ErrorMessage error={error} />
      </div>
    </div>
  );
}

function VendorInfoLabelsPanel({
  form,
  set,
  save,
  saving,
}: {
  form: Partial<PortalSettings>;
  set: <K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) => void;
  save: () => void;
  saving: boolean;
}) {
  const labels = form.companyInfoLabels ?? {};

  function setLabel(key: keyof typeof COMPANY_INFO_LABEL_DEFAULTS, value: string) {
    const trimmed = value.trim();
    const next = { ...(form.companyInfoLabels ?? {}) };
    if (trimmed.length === 0) {
      delete (next as Record<string, unknown>)[key];
    } else {
      (next as Record<string, string>)[key] = trimmed;
    }
    set('companyInfoLabels', Object.keys(next).length === 0 ? undefined : next);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Vendor Information</h2>
        <Btn onClick={save} loading={saving}>
          Save
        </Btn>
      </div>
      <p className="text-xs text-slate-500">
        Rename the labels shown on the admin invite form and the vendor&apos;s questionnaire. Leave
        blank to use the default label.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(
          Object.entries(COMPANY_INFO_LABEL_DEFAULTS) as [
            keyof typeof COMPANY_INFO_LABEL_DEFAULTS,
            string,
          ][]
        ).map(([key, fallback]) => (
          <Field key={key} label={`${key} label`} hint={`Default: ${fallback}`}>
            <TextInput
              value={labels[key] ?? ''}
              placeholder={fallback}
              onChange={(e) => setLabel(key, e.target.value)}
            />
          </Field>
        ))}
      </div>
    </div>
  );
}

function OptionListPanel({
  title,
  hint,
  value,
  savedValue,
  onChange,
  itemLabel,
  itemLabelPlural,
  addLabel,
  placeholder,
  settingsKey,
  saveList,
  saving,
}: {
  title: string;
  hint: string;
  value: string[];
  savedValue: string[];
  onChange: (list: string[]) => void;
  itemLabel: string;
  itemLabelPlural: string;
  addLabel: string;
  placeholder: string;
  settingsKey: OptionListKey;
  saveList: (key: OptionListKey, list: string[]) => void;
  saving: boolean;
}) {
  // Bumped on every save so the editor remounts and re-seeds its rows from the
  // freshly sorted `value`. OptionListEditor ignores `value` changes by design
  // (its raw rows are the source of truth while typing), so a remount is the
  // only way to show the new order — which is exactly the behaviour wanted
  // here: the list alphabetises on Save, not as the admin types.
  const [seedKey, setSeedKey] = useState(0);
  // Held above that `key` so a save re-seeds the rows without discarding the
  // admin's search text.
  const [search, setSearch] = useState('');

  function handleSave() {
    saveList(settingsKey, value);
    setSeedKey((k) => k + 1);
  }

  return (
    <OptionListEditor
      key={seedKey}
      title={title}
      hint={hint}
      value={value}
      savedValue={savedValue}
      onChange={onChange}
      onSave={handleSave}
      saving={saving}
      itemLabel={itemLabel}
      itemLabelPlural={itemLabelPlural}
      addLabel={addLabel}
      placeholder={placeholder}
      search={search}
      onSearchChange={setSearch}
      maxLength={120}
    />
  );
}

function NotificationsPanel({
  form,
  set,
  save,
  saving,
  error,
}: {
  form: Partial<PortalSettings>;
  set: <K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) => void;
  save: () => void;
  saving: boolean;
  error: unknown;
}) {
  const days = form.documentExpiryReminderDays ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Notifications</h2>
        <Btn onClick={save} loading={saving}>
          Save
        </Btn>
      </div>
      {/*
        The reminder-interval input that used to sit below this list was removed
        on 2026-08-11. It rendered `form.documentReminderIntervalDays ?? 14`, so
        it displayed 14 while storing nothing — and any save from this panel then
        posted that phantom 14 back, throttling the whole reminder pass to
        fortnightly and silently killing every threshold shorter than 14 days.
        The list below is now the only reminder control.
      */}
      <p className="text-xs text-slate-500">
        Email the vendor a reminder this many days before an uploaded document expires. Add multiple
        values to send more than one reminder (e.g. 30, 20 and 1 day out) — each document gets one
        email per value. Remove every value to turn expiry reminders off. Until this is configured,
        the portal reminds once, 14 days out.
      </p>
      <StringListEditor
        value={days.map(String)}
        onChange={(list) =>
          set('documentExpiryReminderDays', normalizeReminderDays(list.map(Number)))
        }
        itemLabel="reminder"
        addLabel="Add reminder"
        placeholder="e.g. 30"
        maxLength={3}
      />
      <ErrorMessage error={error} />
    </div>
  );
}

function DocumentAnalysisPanel({
  form,
  set,
  save,
  saving,
  error,
}: {
  form: Partial<PortalSettings>;
  set: <K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) => void;
  save: () => void;
  saving: boolean;
  error: unknown;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Document Analysis</h2>
        <Btn onClick={save} loading={saving}>
          Save
        </Btn>
      </div>
      <p className="text-xs text-slate-500">
        The master switch for AI document analysis. When enabled, documents uploaded with a
        questionnaire submission are analysed on the questions that opt in — enable AI and set the
        criteria per question in the question builder. Switching this off stops all new analysis,
        including the Retry button on a vendor's documents. The result is advisory only — it never
        blocks a submission and vendors never see it.
      </p>
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <input
          type="checkbox"
          checked={form.documentAnalysisEnabled ?? false}
          onChange={(e) => set('documentAnalysisEnabled', e.target.checked)}
        />
        Analyse uploaded documents on submit
      </label>
      <ErrorMessage error={error} />
    </div>
  );
}

function QuestionCategoriesPanel() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: ['question-categories', 'all'],
    queryFn: () =>
      adminApi.get<{ categories: QuestionCategory[] }>(
        '/api/question-categories?include_archived=1',
      ),
  });

  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState<unknown>(null);

  const allCategories = data?.categories ?? [];
  const categories = allCategories.filter((c) => !c.deleted_at);
  const archived = allCategories.filter((c) => c.deleted_at);

  const createMutation = useMutation({
    mutationFn: (input: { name: string }) => adminApi.post('/api/question-categories', input),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
    onError: (err) => setError(err),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      adminApi.patch(`/api/question-categories/${input.id}`, { name: input.name }),
    onSuccess: () => {
      setEditingId(null);
      setEditingName('');
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
    onError: (err) => setError(err),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/question-categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
    onError: (err) => setError(err),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => adminApi.post(`/api/question-categories/${id}/restore`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.post('/api/question-categories/reorder', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
    onError: (err) => setError(err),
  });

  function move(id: string, dir: -1 | 1) {
    const ids = categories.map((c) => c.id);
    const idx = ids.indexOf(id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= ids.length) return;
    [ids[idx], ids[next]] = [ids[next]!, ids[idx]!];
    reorderMutation.mutate(ids);
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Question categories
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Categories are shared across all questionnaires and group questions into wizard steps.
          Parent/child links may cross categories — pick any question in the same questionnaire as a
          parent when setting up conditional logic.
        </p>
      </div>

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const trimmed = name.trim();
          if (!trimmed) return;
          createMutation.mutate({ name: trimmed });
        }}
      >
        <div className="flex-1">
          <Field label="Add category">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Vehicle safety"
            />
          </Field>
        </div>
        <Btn type="submit" loading={createMutation.isPending} disabled={!name.trim()}>
          Add
        </Btn>
      </form>

      {isLoading ? (
        <CenteredSpinner />
      ) : categories.length === 0 ? (
        <EmptyState
          title="No categories yet"
          description="Add a category above to start organising questions."
        />
      ) : (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
          {categories.map((cat, i) => (
            <li key={cat.id} className="flex items-center gap-3 px-3 py-2">
              <div className="flex flex-col">
                <button
                  onClick={() => move(cat.id, -1)}
                  disabled={i === 0}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => move(cat.id, 1)}
                  disabled={i === categories.length - 1}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move down"
                >
                  ▼
                </button>
              </div>
              <div className="flex-1">
                {editingId === cat.id ? (
                  <TextInput
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && editingName.trim()) {
                        updateMutation.mutate({ id: cat.id, name: editingName.trim() });
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="text-sm text-slate-900">{cat.name}</span>
                )}
              </div>
              {editingId === cat.id ? (
                <>
                  <Btn
                    size="sm"
                    onClick={() => updateMutation.mutate({ id: cat.id, name: editingName.trim() })}
                    loading={updateMutation.isPending}
                    disabled={!editingName.trim()}
                  >
                    Save
                  </Btn>
                  <Btn size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </Btn>
                </>
              ) : (
                <>
                  <Btn
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditingId(cat.id);
                      setEditingName(cat.name);
                    }}
                  >
                    Rename
                  </Btn>
                  <Btn
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      void (async () => {
                        if (
                          await confirm({
                            title: 'Archive category',
                            body: (
                              <>
                                Archive <span className="font-semibold">{cat.name}</span>? Its
                                questions stay in their questionnaires with all answers — it is just
                                removed from the category list and can be restored later.
                              </>
                            ),
                            confirmLabel: 'Archive',
                          })
                        )
                          deleteMutation.mutate(cat.id);
                      })();
                    }}
                  >
                    Archive
                  </Btn>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {archived.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Archived
          </h3>
          <div className="space-y-2">
            {archived.map((cat) => (
              <div
                key={cat.id}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <span className="text-sm text-slate-500">{cat.name}</span>
                <Btn size="sm" variant="secondary" onClick={() => restoreMutation.mutate(cat.id)}>
                  Restore
                </Btn>
              </div>
            ))}
          </div>
        </div>
      )}

      <ErrorMessage error={error} />
    </div>
  );
}
